import { PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from 'react'
import { Plane, Vector2, Vector3 } from 'three'
import { ImmutableVector3, useDocumentListener } from '@modugen/ui-toolkit'
import { useThree } from '@react-three/fiber'
import { useIfcStore } from '@stores'
import {
  ROTATION_SNAP_RAD,
  ROTATION_SNAP_TOLERANCE_RAD,
  getFrameAxisAngle,
  rotateAroundZ,
} from './utils'

/** `xy`/`z` translate the whole picked frame; `rotate` spins it around the middle (second) point. */
export type IfcAlignmentDragMode = 'xy' | 'z' | 'rotate'

interface DragContext {
  mode: IfcAlignmentDragMode
  plane: Plane
  /** World-space offset between the grabbed plane point and the pivot (translate modes). */
  offset: Vector3
  /** The middle (second) point — translation reference and rotation pivot. */
  center: Vector3
  /** Pointer angle around `center` at drag start (rotate mode). */
  startAngle: number
  /** The frame's X-axis angle (first→second vs. global X) at drag start (rotate mode). */
  baseAxisAngle: number
  /** Whether the rotation magnetically snaps near 90° multiples (rotate mode). */
  snapRotation: boolean
  origFirst: ImmutableVector3
  origSecond: ImmutableVector3
  origThird: ImmutableVector3
}

const translateXY = (p: ImmutableVector3, dx: number, dy: number): ImmutableVector3 =>
  new ImmutableVector3(p.x + dx, p.y + dy, p.z)

const translateZ = (p: ImmutableVector3, dz: number): ImmutableVector3 =>
  new ImmutableVector3(p.x, p.y, p.z + dz)

interface IfcAlignmentPointsDrag {
  /**
   * Begin dragging the picked frame from a HUD button pointer event. `snapRotation` (rotate mode)
   * enables magnetic snapping near 90° multiples; defaults to true.
   */
  startDrag: (mode: IfcAlignmentDragMode, event: ReactPointerEvent, snapRotation?: boolean) => void
  /** Which mode is currently dragging (drives the HUD button's active style). */
  activeDragMode: IfcAlignmentDragMode | null
}

/**
 * Drag-to-adjust behaviour for the three picked IFC alignment points, mirroring the comment HUD:
 * a HUD button's pointer-down starts a drag, document pointer move/up track it, and the moved
 * points are written straight to the (local) ifc store. `xy`/`z` translate all three points
 * together; `rotate` spins the origin/third points around the middle point to change the angle.
 */
const useIfcAlignmentPointsDrag = (): IfcAlignmentPointsDrag => {
  const camera = useThree(state => state.camera)
  const gl = useThree(state => state.gl)
  const raycaster = useThree(state => state.raycaster)

  const [activeDragMode, setActiveDragMode] = useState<IfcAlignmentDragMode | null>(null)
  const dragRef = useRef<DragContext | null>(null)

  const isDragging = activeDragMode !== null

  const getWorldOnPlane = useCallback(
    (clientX: number, clientY: number, plane: Plane): Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect()
      const ndc = new Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      )
      raycaster.setFromCamera(ndc, camera)
      const target = new Vector3()
      return raycaster.ray.intersectPlane(plane, target) ? target.clone() : null
    },
    [camera, gl, raycaster],
  )

  useDocumentListener(
    'pointermove',
    (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const world = getWorldOnPlane(event.clientX, event.clientY, drag.plane)
      if (!world) return

      const { setFirstPoint, setSecondPoint, setThirdPoint } = useIfcStore.getState()

      if (drag.mode === 'rotate') {
        const angle = Math.atan2(world.y - drag.center.y, world.x - drag.center.x)
        const rawDelta = angle - drag.startAngle
        // Rotate freely, but magnetically snap the frame's absolute orientation (its X axis vs. the
        // global X axis) to the nearest 90° multiple when close to it — so it locks to 0/90/180/270°
        // in world space, not relative to the drag start. Snapping can be toggled off from the HUD.
        const absoluteAxisAngle = drag.baseAxisAngle + rawDelta
        const nearest = Math.round(absoluteAxisAngle / ROTATION_SNAP_RAD) * ROTATION_SNAP_RAD
        const delta =
          drag.snapRotation && Math.abs(absoluteAxisAngle - nearest) <= ROTATION_SNAP_TOLERANCE_RAD
            ? nearest - drag.baseAxisAngle
            : rawDelta
        setFirstPoint(rotateAroundZ(drag.origFirst, drag.center, delta))
        setThirdPoint(rotateAroundZ(drag.origThird, drag.center, delta))
        return
      }

      const target = world.clone().sub(drag.offset)
      if (drag.mode === 'xy') {
        const dx = target.x - drag.center.x
        const dy = target.y - drag.center.y
        setFirstPoint(translateXY(drag.origFirst, dx, dy))
        setSecondPoint(translateXY(drag.origSecond, dx, dy))
        setThirdPoint(translateXY(drag.origThird, dx, dy))
      } else {
        const dz = target.z - drag.center.z
        setFirstPoint(translateZ(drag.origFirst, dz))
        setSecondPoint(translateZ(drag.origSecond, dz))
        setThirdPoint(translateZ(drag.origThird, dz))
      }
    },
    [getWorldOnPlane],
    isDragging,
  )

  useDocumentListener(
    'pointerup',
    () => {
      dragRef.current = null
      setActiveDragMode(null)
    },
    [],
    isDragging,
  )

  const startDrag = useCallback(
    (mode: IfcAlignmentDragMode, event: ReactPointerEvent, snapRotation = true) => {
      event.stopPropagation()

      const { firstPoint, secondPoint, thirdPoint } = useIfcStore.getState()
      if (!firstPoint || !secondPoint || !thirdPoint) return

      const center = new Vector3(secondPoint.x, secondPoint.y, secondPoint.z)

      let plane: Plane
      if (mode === 'z') {
        const cameraDirection = camera.getWorldDirection(new Vector3())
        const normal = new Vector3(cameraDirection.x, cameraDirection.y, 0)
        if (normal.lengthSq() < 1e-6) normal.set(1, 0, 0)
        normal.normalize()
        plane = new Plane().setFromNormalAndCoplanarPoint(normal, center)
      } else {
        plane = new Plane(new Vector3(0, 0, 1), -center.z)
      }

      const world = getWorldOnPlane(event.clientX, event.clientY, plane)
      const offset = world ? world.clone().sub(center) : new Vector3()
      const startAngle = world ? Math.atan2(world.y - center.y, world.x - center.x) : 0
      const baseAxisAngle = getFrameAxisAngle(firstPoint, secondPoint)

      dragRef.current = {
        mode,
        plane,
        offset,
        center,
        startAngle,
        baseAxisAngle,
        snapRotation,
        origFirst: firstPoint,
        origSecond: secondPoint,
        origThird: thirdPoint,
      }
      setActiveDragMode(mode)
    },
    [camera, getWorldOnPlane],
  )

  return { startDrag, activeDragMode }
}

export default useIfcAlignmentPointsDrag
